import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import useFocusTrap from '../hooks/useFocusTrap';

/**
 * CommandPalette — Ctrl+K / Cmd+K ile açılan komut paleti.
 * Kategoriler: pages, actions, theme, language.
 * Klavye nav: ↑/↓ seçim, Enter execute, Esc kapat.
 * Fuzzy: lowercase().includes() basit eşleşme.
 */
export default function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const { t, setLang, lang } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const { logout, user } = useAuth();

  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const dialogRef = useRef(null);
  useFocusTrap(open, dialogRef, { onEscape: onClose });

  // Komut listesi — group=page|action|theme|language
  const commands = useMemo(() => {
    const setLight = () => { if (isDark) toggleTheme(); };
    const setDark  = () => { if (!isDark) toggleTheme(); };
    const isCashier = user?.role === 'cashier';
    const all = [
      // Sayfalar
      { id: 'p-dashboard',  group: 'page',     icon: '📊', label: t('cmdPaletteGoDashboard')  || 'Dashboard',          action: () => navigate('/'),                cashier: false },
      { id: 'p-tables',     group: 'page',     icon: '🍽️', label: t('cmdPaletteGoTables')     || 'Masalar',            action: () => navigate('/tables'),          cashier: false },
      { id: 'p-orders',     group: 'page',     icon: '📋', label: t('cmdPaletteGoOrders')     || 'Siparişler',         action: () => navigate('/orders'),          cashier: false },
      { id: 'p-kitchen',    group: 'page',     icon: '👨‍🍳', label: t('cmdPaletteGoKitchen')    || 'Mutfak',             action: () => navigate('/kitchen'),         cashier: false },
      { id: 'p-pos',        group: 'page',     icon: '💳', label: t('cmdPaletteGoPOS')        || 'POS',                action: () => navigate('/pos'),             cashier: true  },
      { id: 'p-customers',  group: 'page',     icon: '👥', label: t('cmdPaletteGoCustomers')  || 'Müşteriler',         action: () => navigate('/customers'),       cashier: false },
      { id: 'p-menu',       group: 'page',     icon: '🍔', label: t('cmdPaletteGoMenu')       || 'Menü Yönetimi',      action: () => navigate('/menu-management'), cashier: false },
      { id: 'p-stock',      group: 'page',     icon: '📦', label: t('cmdPaletteGoStock')      || 'Stok',               action: () => navigate('/stock'),           cashier: false },
      { id: 'p-reports',    group: 'page',     icon: '📈', label: t('cmdPaletteGoReports')    || 'Raporlar',           action: () => navigate('/reports'),         cashier: false },
      { id: 'p-settings',   group: 'page',     icon: '⚙️', label: t('cmdPaletteGoSettings')   || 'Ayarlar',            action: () => navigate('/settings'),        cashier: false },
      // Tur 315 — yeni sayfa shortcut'ları (cashier görmesin)
      { id: 'p-hr',           group: 'page', icon: '👤', label: t('cmdPaletteGoHR')           || (t('nav_hr')           || 'Personel'),       action: () => navigate('/hr'),           cashier: false },
      { id: 'p-users',        group: 'page', icon: '👥', label: t('cmdPaletteGoUsers')        || (t('nav_users')        || 'Kullanıcılar'),   action: () => navigate('/users'),        cashier: false },
      { id: 'p-reservations', group: 'page', icon: '📅', label: t('cmdPaletteGoReservations') || (t('nav_reservations') || 'Rezervasyonlar'), action: () => navigate('/reservations'), cashier: false },
      { id: 'p-trash',        group: 'page', icon: '🗑️', label: t('cmdPaletteGoTrash')        || (t('trashTitle')       || 'Çöp Kutusu'),    action: () => navigate('/trash'),        cashier: false },
      // Aksiyonlar
      { id: 'a-new-order',    group: 'action',  icon: '🛒', label: t('cmdPaletteNewOrder')    || 'Yeni Sipariş',  action: () => navigate('/pos'),       cashier: true  },
      { id: 'a-new-customer', group: 'action',  icon: '➕', label: t('cmdPaletteNewCustomer') || 'Yeni Müşteri',  action: () => navigate('/customers'), cashier: false },
      { id: 'a-new-staff',    group: 'action',  icon: '👤', label: t('cmdPaletteNewStaff')    || 'Yeni Personel', action: () => navigate('/users'),     cashier: false },
      { id: 'a-logout',       group: 'action',  icon: '🚪', label: t('cmdPaletteLogout')      || 'Çıkış Yap',     action: () => { logout(); navigate('/login'); }, cashier: true },
      // Tema
      { id: 't-light',  group: 'theme',  icon: '☀️', label: t('cmdPaletteThemeLight')  || 'Açık Tema',  action: setLight, cashier: true },
      { id: 't-dark',   group: 'theme',  icon: '🌙', label: t('cmdPaletteThemeDark')   || 'Koyu Tema',  action: setDark,  cashier: true },
      // Dil
      { id: 'l-tr', group: 'language', icon: '🇹🇷', label: t('cmdPaletteLangTR') || 'Türkçe',   action: () => setLang('tr'), cashier: true },
      { id: 'l-en', group: 'language', icon: '🇬🇧', label: t('cmdPaletteLangEN') || 'English',  action: () => setLang('en'), cashier: true },
      { id: 'l-ar', group: 'language', icon: '🇸🇦', label: t('cmdPaletteLangAR') || 'العربية',  action: () => setLang('ar'), cashier: true },
    ];
    return all.filter(c => !isCashier || c.cashier).map(({ cashier, ...rest }) => rest);
  }, [t, navigate, isDark, toggleTheme, logout, setLang, user]);

  // Fuzzy filter: query lowercase substring
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter(c => c.label.toLowerCase().includes(q) || c.group.includes(q));
  }, [query, commands]);

  // Group bazlı sıralama (page → action → theme → language)
  const grouped = useMemo(() => {
    const order = ['page', 'action', 'theme', 'language'];
    const groups = {};
    filtered.forEach(c => { (groups[c.group] = groups[c.group] || []).push(c); });
    return order.filter(g => groups[g]).map(g => ({ group: g, items: groups[g] }));
  }, [filtered]);

  const flat = useMemo(() => grouped.flatMap(g => g.items), [grouped]);

  // Reset state when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Selected index out-of-bound guard
  useEffect(() => {
    if (selectedIndex >= flat.length) setSelectedIndex(Math.max(0, flat.length - 1));
  }, [flat.length, selectedIndex]);

  // Klavye nav
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, flat.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const cmd = flat[selectedIndex];
        if (cmd) { cmd.action(); onClose(); }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, flat, selectedIndex, onClose]);

  // Selected item scroll-into-view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-cmd-idx="${selectedIndex}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex, open]);

  if (!open) return null;

  const groupLabel = (g) => ({
    page:     t('cmdPaletteGroupPages')     || 'Sayfalar',
    action:   t('cmdPaletteGroupActions')   || 'Aksiyonlar',
    theme:    t('cmdPaletteGroupTheme')     || 'Tema',
    language: t('cmdPaletteGroupLanguage')  || 'Dil',
  }[g] || g);

  let runningIdx = -1;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-start justify-center pt-24 px-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cmdpalette-title"
    >
      <div
        ref={dialogRef}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden border border-gray-200 dark:border-gray-700"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <span aria-hidden="true" className="text-gray-400 text-lg">🔍</span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setSelectedIndex(0); }}
            placeholder={t('cmdPalettePlaceholder') || 'Komut ara veya seç...'}
            className="flex-1 bg-transparent outline-none text-gray-900 dark:text-white placeholder-gray-400"
            aria-label={t('cmdPalettePlaceholder') || 'Komut ara'}
          />
          <h2 id="cmdpalette-title" className="sr-only">
            {t('cmdPaletteTitle') || 'Komut Paleti'}
          </h2>
          <kbd className="hidden sm:inline-flex items-center px-2 h-6 text-[10px] font-mono font-semibold bg-gray-100 dark:bg-gray-700 text-gray-500 rounded border border-gray-300 dark:border-gray-600">Esc</kbd>
        </div>
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto py-2">
          {flat.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-400">
              {t('cmdPaletteNoResults') || 'Sonuç bulunamadı'}
            </div>
          ) : grouped.map(g => (
            <div key={g.group} className="mb-1">
              <div className="px-4 py-1 text-[11px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500">
                {groupLabel(g.group)}
              </div>
              {g.items.map(item => {
                runningIdx += 1;
                const idx = runningIdx;
                const isActive = idx === selectedIndex;
                return (
                  <button
                    key={item.id}
                    type="button"
                    data-cmd-idx={idx}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    onClick={() => { item.action(); onClose(); }}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left transition-colors ${isActive ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                  >
                    <span aria-hidden="true" className="text-base leading-none">{item.icon}</span>
                    <span className="flex-1">{item.label}</span>
                    {item.group === 'language' && lang === item.id.split('-')[1] && (
                      <span className="text-[10px] text-blue-500 font-semibold">●</span>
                    )}
                    {isActive && <span className="text-[10px] text-gray-400">↵</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 dark:border-gray-700 text-[11px] text-gray-400">
          <span>{t('cmdPaletteHintNav') || '↑↓ ile gez, Enter ile seç'}</span>
          <span>{t('cmdPaletteHintClose') || 'Esc ile kapat'}</span>
        </div>
      </div>
    </div>
  );
}
